export class TextHelper {
    static makeUrlsLinks(html: string): string {
        html ??= '';
        const value = [...html];
        const matches = [...html.matchAll(/(\b(https?:\/\/|www\.)[^\s]+)/gm)].reverse();

        for (const match of matches) {
            const [url] = match;
            const href = url.startsWith('http') ? url : ('https://' + url);

            if (html[match.index - 1] !== '"' && !(html[match.index - 1] === '>' && html[match.index - 2] === '"')) {
                const link = document.createElement('a');
                link.href = href.startsWith('http://') ? `https://corsproxy.io/?${href}` : href;
                link.target = '_blank';
                link.innerText = url;
                value.splice(match.index, url.length, ...link.outerHTML);
            }
        }

        return value.join('');
    }

    /**
     * A document with no browsing context. Parsing into it is inert: resources
     * are never fetched and event handlers such as `onerror` never fire, which
     * is not true of an ordinary element even when it is never inserted.
     */
    private static inertDocument: Document | null = null;

    private static parseInert(html: string): Document {
        if (!TextHelper.inertDocument) {
            TextHelper.inertDocument = document.implementation.createHTMLDocument('');

            // Without this the document's base URL is about:blank and relative
            // URLs stop resolving, which would change how <a href> is read back.
            const base = TextHelper.inertDocument.createElement('base');
            base.href = document.baseURI;
            TextHelper.inertDocument.head.append(base);
        }

        TextHelper.inertDocument.body.innerHTML = html ?? '';

        return TextHelper.inertDocument;
    }

    /**
     * Decodes an HTML string to plain text.
     *
     * Output is identical to the detached-element `innerText` this replaces: on
     * an element that is not being rendered, `innerText` returns `textContent`.
     */
    static decodeHtml(html: string): string {
        return TextHelper.parseInert(html).body.textContent ?? '';
    }

    /**
     * Decodes an HTML string to plain text, keeping block boundaries as line
     * breaks the way `innerText` does for a rendered element. Use this where the
     * text was previously read from an element attached to the document.
     */
    static htmlToText(html: string): string {
        const doc = TextHelper.parseInert(html);
        doc.body.querySelectorAll('br, p, div, li, tr, h1, h2, h3, h4, h5, h6')
            .forEach(el => el.before('\n'));

        return doc.body.textContent ?? '';
    }

    /** Escapes text for safe interpolation into an HTML string. */
    static escapeHtml(text: string): string {
        return String(text ?? '')
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');
    }

    static getInnerText(text: string) {
        return TextHelper.decodeHtml(text);
    }

    static sanitizeHtml(html: string): string {
        if (!html) {
            return html;
        }

        function * getSafeNodes(root: Node): Iterable<Node> {
            if (root instanceof Text) {
                yield root.cloneNode();
                return;
            }

            if (root instanceof HTMLElement) {
                if ([
                    'P', 'A', 'B', 'U', 'I', 'EM', 'STRONG', 'BR', 'HR',
                    'FONT', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6',
                    'SUP', 'SUB', 'INS', 'DEL', 'MARK', 'UL', 'OL', 'LI',
                ].includes(root.tagName)) {
                    const newEl = document.createElement(root.tagName);

                    for (let i = 0; i < root.childNodes.length; ++i) {
                        for (const newChild of getSafeNodes(root.childNodes[i])) {
                            newEl.append(newChild);
                        }
                    }

                    if (root instanceof HTMLAnchorElement && newEl instanceof HTMLAnchorElement) {
                        if (root.target === '_blank' || root.target === 'blank') {
                            newEl.target = '_blank';
                        }

                        try {
                            const url = new URL(root.href);

                            if (url.protocol === 'http:' || url.protocol === 'https:') {
                                newEl.href = url.href;
                            }

                        } catch (e) {

                        }
                    }

                    if (root instanceof HTMLFontElement && newEl instanceof HTMLFontElement) {
                        if (root.color) {
                            newEl.color = root.color;
                        }

                        if (root.size) {
                            newEl.size = root.size;
                        }
                    }

                    yield newEl;
                } else {
                    for (let i = 0; i < root.childNodes.length; ++i) {
                        for (const newChild of getSafeNodes(root.childNodes[i])) {
                            yield newChild;
                        }
                    }

                    if (root instanceof HTMLDivElement) {
                        yield document.createElement('br');
                    }
                }
            }
        }

        // Parsed inertly: the untrusted markup never becomes live nodes, so
        // handlers such as onerror cannot fire during the parse this sanitizer
        // performs. Only allowlisted, resource-free elements reach the span.
        const nodes = getSafeNodes(TextHelper.parseInert(html).body);
        const span = document.createElement('span');
        span.replaceChildren(...nodes);

        return span.innerHTML;
    }
}
